import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { Save, Upload, Eye, EyeOff, Plus, Pencil, Trash2, RefreshCw, Send, Check, Loader2, FileText, Bell as BellIcon, Lock as LockIcon, X } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  DEFAULT_STAR_FORMULA, DEFAULT_NEEDS_ATTENTION, normalizeFormula, normalizeThresholds, formulaPreview,
} from '../lib/starScore';
import IssueGuardrails from './IssueGuardrails';
import QualityRubric from './QualityRubric';
import DeveloperApi from './DeveloperApi';
import { normalizeOutcomeOptions, slugifyOutcome } from '../lib/issueOutcomes';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'usage', label: 'Usage' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'scoring', label: 'Scoring' },
  // { id: 'defaults', label: 'Estimated Margins' }, // hidden per request, restore by uncommenting
  { id: 'rubric', label: 'Quality Rubric' },
  { id: 'curriculum', label: 'Curriculum Checks' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'api', label: 'API' },
  // { id: 'notifications', label: 'Notifications' }, // hidden per request, restore by uncommenting
  { id: 'guardrails', label: 'Issue Guardrails' },
  { id: 'residency', label: 'Data Residency' },
  // { id: 'team', label: 'Team' }, // hidden per request, restore by uncommenting
];

const COUNTRIES = [
  'Andorra', 'Australia', 'Austria', 'Belgium', 'Canada', 'Denmark', 'Finland',
  'France', 'Germany', 'Greece', 'Iceland', 'Ireland', 'Italy', 'Luxembourg',
  'Malta', 'Monaco', 'Netherlands', 'New Zealand', 'Norway', 'Portugal',
  'San Marino', 'Spain', 'Sweden', 'Switzerland', 'United Kingdom',
  'United States', 'Vatican City',
];
const PHONE_CODES = [
  { code: '+61', label: '+61 Australia' },
  { code: '+1', label: '+1 United States / Canada' },
  { code: '+44', label: '+44 United Kingdom' },
  { code: '+64', label: '+64 New Zealand' },
  { code: '+353', label: '+353 Ireland' },
  { code: '+33', label: '+33 France' },
  { code: '+49', label: '+49 Germany' },
  { code: '+34', label: '+34 Spain' },
  { code: '+39', label: '+39 Italy' },
  { code: '+31', label: '+31 Netherlands' },
  { code: '+32', label: '+32 Belgium' },
  { code: '+41', label: '+41 Switzerland' },
  { code: '+43', label: '+43 Austria' },
  { code: '+45', label: '+45 Denmark' },
  { code: '+46', label: '+46 Sweden' },
  { code: '+47', label: '+47 Norway' },
  { code: '+358', label: '+358 Finland' },
  { code: '+354', label: '+354 Iceland' },
  { code: '+351', label: '+351 Portugal' },
  { code: '+352', label: '+352 Luxembourg' },
  { code: '+356', label: '+356 Malta' },
  { code: '+30', label: '+30 Greece' },
  { code: '+377', label: '+377 Monaco' },
  { code: '+376', label: '+376 Andorra' },
  { code: '+378', label: '+378 San Marino' },
  { code: '+379', label: '+379 Vatican City' },
];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'NZD', 'SGD', 'HKD', 'JPY', 'INR'];
const TIMEZONES = [
  'Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Perth',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'Europe/London', 'Europe/Paris', 'Asia/Singapore', 'Asia/Hong_Kong', 'Pacific/Auckland',
];
const INDICATOR_LABELS = {
  tutor_lateness: 'Tutor Lateness',
  incorrect_information: 'Incorrect Information',
  poor_conduct: 'Poor Conduct/Professionalism',
  poaching: 'Poaching Attempts',
  speaking_poorly: 'Speaking Poorly of Organization',
  session_cut_short: 'Session Cut Short',
  giving_answers: 'Giving Answers (Not Teaching)',
  lack_of_preparation: 'Lack of Preparation',
  dismissive_of_questions: 'Dismissive of Questions',
  confusing_explanations: 'Confusing Explanations',
};
const ROLES = ['admin', 'manager', 'reviewer', 'read-only'];
const DATA_REGIONS = [
  { value: 'au-sydney', label: 'Australia (Sydney)' },
  { value: 'us-oregon', label: 'United States (Oregon)' },
  { value: 'eu-frankfurt', label: 'Europe (Frankfurt)' },
  { value: 'uk-london', label: 'United Kingdom (London)' },
  { value: 'ap-singapore', label: 'Singapore' },
];

const Settings = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, organizationId, orgName, developerAccess } = useAuth();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  // General
  const [profile, setProfile] = useState({
    name: '', business_type: '', company_number: '', country: '', business_address: '',
    phone_country_code: '+61', phone_number: '', contact_email: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // Analysis
  const [analysis, setAnalysis] = useState({
    lateness_minor_minutes: 3, lateness_moderate_minutes: 6, lateness_severe_minutes: 9,
    indicator_weights: {},
  });
  const [issueOutcomes, setIssueOutcomes] = useState(normalizeOutcomeOptions());
  const [newIssueOutcome, setNewIssueOutcome] = useState('');

  // Default Metrics
  const [defaults, setDefaults] = useState({
    currency: 'AUD', default_lesson_time_minutes: 60,
    default_tutor_hourly_rate: 0, default_billable_hourly_rate: 0,
  });

  // Scoring
  const toPct = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, Math.round((Number(v) || 0) * 100)]));
  const fromPct = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, (Number(v) || 0) / 100]));
  const [starWeights, setStarWeights] = useState(toPct(DEFAULT_STAR_FORMULA.weights));
  const [qualityDims, setQualityDims] = useState(toPct(DEFAULT_STAR_FORMULA.quality_dimensions));
  const [needsAttn, setNeedsAttn] = useState({ ...DEFAULT_NEEDS_ATTENTION });

  // Integrations
  const [lsApiKey, setLsApiKey] = useState('');
  const [showLsKey, setShowLsKey] = useState(false);
  const [lsSyncEnabled, setLsSyncEnabled] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncJobs, setSyncJobs] = useState([]);
  const [zoomConfig, setZoomConfig] = useState({ account_id: '', client_id: '', client_secret: '' });
  const [showZoomSecret, setShowZoomSecret] = useState(false);

  // Google Meet
  const [meetConfig, setMeetConfig] = useState({ service_account_json: '', impersonate_subject: '' });
  const [showMeetJson, setShowMeetJson] = useState(false);
  const [meetSaving, setMeetSaving] = useState(false);
  const [meetConfigured, setMeetConfigured] = useState(false);

  // Integrations lock (server-side verified developer code — never stored in this file)
  const [integrationsUnlocked, setIntegrationsUnlocked] = useState(
    () => sessionStorage.getItem('tt_integrations_unlocked') === '1'
  );
  const [devCode, setDevCode] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  // Notifications
  const [notifications, setNotifications] = useState({
    highRiskAlert: true, dailyReport: false, weeklyReport: false,
    emailMarketing: false, dailyReportTime: '08:00', weeklyReportTime: '09:00',
    timezone: 'Australia/Sydney',
  });

  // Team
  const [teamMembers, setTeamMembers] = useState([]);
  const [showAddMember, setShowAddMember] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('reviewer');

  // Email Reports
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const emailDefault = (type) => ({
    notification_type: type, enabled: false,
    recipient_email: '', recipient_name: '',
    recipient_email_2: '', recipient_name_2: '',
    recipient_email_3: '', recipient_name_3: '',
    send_time: '08:00', send_day: 1,
    include_new_sessions: true, include_flagged_issues: true,
    include_star_performers: true, include_grade_distribution: true,
    include_key_metrics: true,
    subject_line: type === 'daily' ? 'TutorTracer Daily Report' : 'TutorTracer Weekly Report',
    greeting_message: '',
  });
  const [dailyEmail, setDailyEmail] = useState(emailDefault('daily'));
  const [weeklyEmail, setWeeklyEmail] = useState(emailDefault('weekly'));
  const [emailSaving, setEmailSaving] = useState(null);
  const [emailSaved, setEmailSaved] = useState(null);
  const [emailTesting, setEmailTesting] = useState(null);

  // Curriculum Checks
  const [curriculumDocs, setCurriculumDocs] = useState([]);
  const [curriculumDocsLoaded, setCurriculumDocsLoaded] = useState(false);
  const [curriculumScopes, setCurriculumScopes] = useState({}); // document_id -> scope rows
  const [participants, setParticipants] = useState([]); // distinct tutor/student pairings with lesson counts
  // Coverage modal: edits a draft, nothing writes until Save.
  const [coverageDoc, setCoverageDoc] = useState(null);
  const [coverageDraft, setCoverageDraft] = useState({ tutors: [], students: [], pairs: [] });
  const [coverageSearch, setCoverageSearch] = useState({ tutors: '', students: '' });
  const [pairForm, setPairForm] = useState({ tutor: '', student: '' });
  const [coverageSaving, setCoverageSaving] = useState(false);

  // Usage
  const [usageStats, setUsageStats] = useState({ totalLessons: 0, totalMinutes: 0, avgPerDay: 0 });
  const [usageChartData, setUsageChartData] = useState([]);

  const cardClass = `rounded-lg border p-5 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-[#09090b] border-[#27272a] text-white' : 'bg-white border-[#e5e5e5] text-[#1a1a1a]'} focus:outline-none focus:ring-1 focus:ring-[#586f8d]`;
  const labelClass = `block text-xs mb-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`;
  const selectClass = inputClass;

  useEffect(() => {
    if (!organizationId) return;
    loadOrg();
  }, [organizationId]);

  useEffect(() => {
    if (activeTab === 'usage' && organizationId) loadUsage();
    if (activeTab === 'team' && organizationId) loadTeam();
    if (activeTab === 'integrations' && organizationId) loadSyncJobs();
    if (activeTab === 'curriculum' && organizationId) loadCurriculumDocs();
    if (activeTab === 'notifications' && organizationId) loadEmailSettings();
  }, [activeTab, organizationId]);

  const loadOrg = async () => {
    setLoading(true);
    try {
      const { data } = await supabase.from('organizations').select('*').eq('id', organizationId).single();
      if (data) {
        setOrg(data);
        const s = data.settings || {};
        const op = s.organization_profile || {};
        setProfile({
          name: data.name || '', business_type: op.business_type || '',
          company_number: op.company_number || '', country: op.country || '',
          business_address: op.business_address || '',
          phone_country_code: op.phone_country_code || '+61',
          phone_number: op.phone_number || '', contact_email: op.contact_email || '',
        });
        if (data.logo_url) setLogoPreview(data.logo_url);
        setAnalysis({
          lateness_minor_minutes: data.lateness_minor_minutes || 3,
          lateness_moderate_minutes: data.lateness_moderate_minutes || 6,
          lateness_severe_minutes: data.lateness_severe_minutes || 9,
          indicator_weights: data.indicator_weights || {},
        });
        setIssueOutcomes(normalizeOutcomeOptions(s.issue_outcome_options));
        setDefaults({
          currency: data.currency || 'AUD',
          default_lesson_time_minutes: data.default_lesson_time_minutes || 60,
          default_tutor_hourly_rate: data.default_tutor_hourly_rate || 0,
          default_billable_hourly_rate: data.default_billable_hourly_rate || 0,
        });
        const f = normalizeFormula(data.star_score_formula);
        setStarWeights(toPct(f.weights));
        setQualityDims(toPct(f.quality_dimensions));
        setNeedsAttn(normalizeThresholds(data.needs_attention_thresholds));
        setLsApiKey(s.lessonspace_api_key || '');
        setLsSyncEnabled(data.lessonspace_sync_enabled || false);
        setZoomConfig({
          account_id: s.zoom_account_id || '',
          client_id: s.zoom_client_id || '',
          client_secret: s.zoom_client_secret || '',
        });
        // Service account JSON is encrypted at rest server-side; never round-tripped to the client.
        setMeetConfig(m => ({ ...m, impersonate_subject: data.meet_impersonate_subject || '' }));
        setMeetConfigured(!!data.meet_sync_enabled);
        const n = s.notifications || {};
        setNotifications({
          highRiskAlert: n.highRiskAlert !== false,
          dailyReport: n.dailyReport || false,
          weeklyReport: n.weeklyReport || false,
          emailMarketing: n.emailMarketing || false,
          dailyReportTime: n.dailyReportTime || '08:00',
          weeklyReportTime: n.weeklyReportTime || '09:00',
          timezone: n.timezone || 'Australia/Sydney',
        });
      }
    } catch (e) {
      console.error('Error loading org:', e);
    }
    setLoading(false);
  };

  const loadTeam = async () => {
    try {
      const { data } = await supabase
        .from('user_organizations')
        .select('id, user_id, role, created_at')
        .eq('organization_id', organizationId);
      if (data) {
        const members = await Promise.all(data.map(async m => {
          const { data: { user: u } } = await supabase.auth.admin.getUserById(m.user_id).catch(() => ({ data: { user: null } }));
          return { ...m, email: u?.email || m.user_id };
        }));
        setTeamMembers(members.length > 0 ? members : data.map(m => ({ ...m, email: m.user_id })));
      }
    } catch {
      // Fallback - just show user_ids
      const { data } = await supabase
        .from('user_organizations')
        .select('id, user_id, role, created_at')
        .eq('organization_id', organizationId);
      setTeamMembers((data || []).map(m => ({ ...m, email: m.user_id })));
    }
  };

  const loadUsage = async () => {
    try {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString();
      const { data } = await supabase
        .from('sessions')
        .select('lesson_timestamp, duration_minutes')
        .eq('organization_id', organizationId)
        .gte('lesson_timestamp', thirtyDaysAgo)
        .order('lesson_timestamp');
      const sessions = data || [];
      const totalMinutes = sessions.reduce((s, r) => s + (r.duration_minutes || 0), 0);

      // Group by day
      const dayMap = {};
      sessions.forEach(s => {
        const day = new Date(s.lesson_timestamp).toLocaleDateString([], { day: 'numeric', month: 'short' });
        dayMap[day] = (dayMap[day] || 0) + 1;
      });
      const chartData = Object.entries(dayMap).map(([date, count]) => ({ date, lessons: count }));

      setUsageStats({
        totalLessons: sessions.length,
        totalMinutes: Math.round(totalMinutes),
        avgPerDay: sessions.length > 0 ? Math.round(sessions.length / 30 * 10) / 10 : 0,
      });
      setUsageChartData(chartData);
    } catch (e) {
      console.error('Error loading usage:', e);
    }
  };

  const loadSyncJobs = async () => {
    try {
      const { data } = await supabase
        .from('sync_jobs')
        .select('*')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .limit(10);
      setSyncJobs(data || []);
    } catch { /* ignore */ }
  };

  const loadEmailSettings = async () => {
    try {
      const { data } = await supabase
        .from('email_notification_settings')
        .select('*')
        .eq('organization_id', organizationId);
      if (data) {
        data.forEach(row => {
          if (row.notification_type === 'daily') setDailyEmail(prev => ({ ...prev, ...row }));
          if (row.notification_type === 'weekly') setWeeklyEmail(prev => ({ ...prev, ...row }));
        });
      }
    } catch (e) {
      console.error('Error loading email settings:', e);
    }
  };

  const handleEmailSave = async (type) => {
    const settings = type === 'daily' ? dailyEmail : weeklyEmail;
    setEmailSaving(type);
    setEmailSaved(null);
    try {
      const payload = {
        organization_id: organizationId, notification_type: type,
        enabled: settings.enabled, recipient_email: settings.recipient_email,
        recipient_name: settings.recipient_name,
        recipient_email_2: settings.recipient_email_2 || null,
        recipient_name_2: settings.recipient_name_2 || null,
        recipient_email_3: settings.recipient_email_3 || null,
        recipient_name_3: settings.recipient_name_3 || null,
        send_time: settings.send_time,
        send_day: type === 'weekly' ? settings.send_day : null,
        include_new_sessions: settings.include_new_sessions,
        include_flagged_issues: settings.include_flagged_issues,
        include_star_performers: settings.include_star_performers,
        include_grade_distribution: settings.include_grade_distribution,
        include_key_metrics: settings.include_key_metrics,
        subject_line: settings.subject_line,
        greeting_message: settings.greeting_message,
      };
      const { error } = await supabase
        .from('email_notification_settings')
        .upsert(payload, { onConflict: 'organization_id,notification_type' });
      if (error) throw error;
      setEmailSaved(type);
      setTimeout(() => setEmailSaved(null), 3000);
      toast.success(`${type === 'daily' ? 'Daily' : 'Weekly'} report settings saved`);
    } catch (e) {
      toast.error('Failed to save email settings');
    }
    setEmailSaving(null);
  };

  const handleEmailTest = async (type) => {
    setEmailTesting(type);
    try {
      const { error } = await supabase.functions.invoke('send-notification-email', {
        body: { organizationId, notificationType: type, isTest: true },
      });
      if (error) throw error;
      toast.success('Test email sent to all configured recipients');
    } catch (e) {
      toast.error('Failed to send test email. Check that edge functions are deployed.');
    }
    setEmailTesting(null);
  };

  const saveGeneral = async () => {
    setSaving(true);
    try {
      let logoUrl = org?.logo_url;
      if (logoFile) {
        const ext = logoFile.name.split('.').pop();
        const path = `${organizationId}/logo.${ext}`;
        await supabase.storage.from('organization-logos').upload(path, logoFile, { upsert: true });
        const { data: { publicUrl } } = supabase.storage.from('organization-logos').getPublicUrl(path);
        logoUrl = publicUrl;
      }

      const settings = { ...(org?.settings || {}), organization_profile: {
        business_type: profile.business_type, company_number: profile.company_number,
        country: profile.country, business_address: profile.business_address,
        phone_country_code: profile.phone_country_code, phone_number: profile.phone_number,
        contact_email: profile.contact_email,
      }};

      await supabase.from('organizations').update({
        name: profile.name, logo_url: logoUrl, settings,
      }).eq('id', organizationId);
      setOrg(prev => ({ ...(prev || {}), name: profile.name, logo_url: logoUrl, settings }));
      toast.success('General settings saved');
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const addIssueOutcome = () => {
    const label = newIssueOutcome.trim();
    if (!label) return;
    const existing = new Set(issueOutcomes.map(o => o.value));
    let value = slugifyOutcome(label);
    let suffix = 2;
    while (existing.has(value)) {
      value = `${slugifyOutcome(label)}_${suffix}`;
      suffix++;
    }
    setIssueOutcomes(prev => [...prev, { value, label }]);
    setNewIssueOutcome('');
  };

  const updateIssueOutcomeLabel = (value, label) => {
    setIssueOutcomes(prev => prev.map(opt => opt.value === value ? { ...opt, label } : opt));
  };

  const removeIssueOutcome = (value) => {
    setIssueOutcomes(prev => {
      const next = prev.filter(opt => opt.value !== value);
      return normalizeOutcomeOptions(next);
    });
  };

  const saveAnalysis = async () => {
    setSaving(true);
    try {
      const settings = {
        ...(org?.settings || {}),
        issue_outcome_options: normalizeOutcomeOptions(issueOutcomes),
      };
      await supabase.from('organizations').update({
        lateness_minor_minutes: parseInt(analysis.lateness_minor_minutes),
        lateness_moderate_minutes: parseInt(analysis.lateness_moderate_minutes),
        lateness_severe_minutes: parseInt(analysis.lateness_severe_minutes),
        indicator_weights: analysis.indicator_weights,
        settings,
      }).eq('id', organizationId);
      setOrg(prev => ({ ...(prev || {}), settings }));
      setIssueOutcomes(normalizeOutcomeOptions(issueOutcomes));
      toast.success('Analysis settings saved');
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const saveDefaults = async () => {
    setSaving(true);
    try {
      await supabase.from('organizations').update({
        currency: defaults.currency,
        default_lesson_time_minutes: parseInt(defaults.default_lesson_time_minutes),
        default_tutor_hourly_rate: parseFloat(defaults.default_tutor_hourly_rate),
        default_billable_hourly_rate: parseFloat(defaults.default_billable_hourly_rate),
      }).eq('id', organizationId);
      toast.success('Default metrics saved');
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const starTotal = Object.values(starWeights).reduce((a, b) => a + (Number(b) || 0), 0);
  const qualityTotal = Object.values(qualityDims).reduce((a, b) => a + (Number(b) || 0), 0);
  const starValid = starTotal === 100;
  const qualityValid = qualityTotal === 100;
  const previewFormula = formulaPreview({
    weights: fromPct(starWeights),
    quality_dimensions: fromPct(qualityDims),
  });

  const saveScoring = async () => {
    if (!starValid || !qualityValid) {
      toast.error('Weights must sum to exactly 100%');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        star_score_formula: {
          weights: fromPct(starWeights),
          quality_dimensions: fromPct(qualityDims),
        },
        needs_attention_thresholds: {
          late_minutes: parseInt(needsAttn.late_minutes) || 0,
          on_time_rate_below_pct: parseInt(needsAttn.on_time_rate_below_pct) || 0,
          quality_below_pct: parseInt(needsAttn.quality_below_pct) || 0,
          min_open_issues: parseInt(needsAttn.min_open_issues) || 0,
          window_days: parseInt(needsAttn.window_days) || 0,
        },
      };
      const { error } = await supabase.from('organizations').update(payload).eq('id', organizationId);
      if (error) throw error;
      toast.success('Scoring settings saved');
    } catch (e) {
      toast.error('Failed to save scoring settings');
    }
    setSaving(false);
  };

  const saveIntegrations = async () => {
    setSaving(true);
    try {
      const settings = { ...(org?.settings || {}),
        lessonspace_api_key: lsApiKey,
        zoom_account_id: zoomConfig.account_id,
        zoom_client_id: zoomConfig.client_id,
        zoom_client_secret: zoomConfig.client_secret,
      };
      await supabase.from('organizations').update({
        settings, lessonspace_sync_enabled: lsSyncEnabled,
      }).eq('id', organizationId);
      toast.success('Integration settings saved');
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const saveNotifications = async () => {
    setSaving(true);
    try {
      const settings = { ...(org?.settings || {}), notifications };
      await supabase.from('organizations').update({ settings }).eq('id', organizationId);
      toast.success('Notification preferences saved');
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const triggerSync = async (source) => {
    setSyncing(true);
    try {
      await supabase.functions.invoke('sync-lessons', { body: { organization_id: organizationId, source } });
      toast.success(`${source} sync started`);
      setTimeout(loadSyncJobs, 3000);
    } catch (e) {
      toast.error('Sync failed to start');
    }
    setSyncing(false);
  };

  const triggerMeetSync = async () => {
    setSyncing(true);
    try {
      await supabase.functions.invoke('sync-googlemeet', { body: { organization_id: organizationId, source: 'googlemeet' } });
      toast.success('googlemeet sync started');
      setTimeout(loadSyncJobs, 3000);
    } catch (e) {
      toast.error('Sync failed to start');
    }
    setSyncing(false);
  };

  const saveMeetCredentials = async () => {
    if (!meetConfig.service_account_json.trim() || !meetConfig.impersonate_subject.trim()) {
      toast.error('Service account JSON and impersonate subject are required');
      return;
    }
    setMeetSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke('save-googlemeet-credentials', {
        body: {
          organizationId,
          serviceAccountJson: meetConfig.service_account_json,
          impersonateSubject: meetConfig.impersonate_subject.trim(),
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setMeetConfigured(true);
      setMeetConfig(m => ({ ...m, service_account_json: '' }));
      toast.success('Google Meet credentials saved');
    } catch (e) {
      toast.error(e?.message || 'Failed to save Google Meet credentials');
    }
    setMeetSaving(false);
  };

  const handleUnlockIntegrations = async () => {
    const code = devCode.trim();
    if (!code) return;
    setUnlocking(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-dev-code', { body: { code } });
      if (error?.context?.status === 429) {
        toast.error('Too many attempts — wait a minute');
      } else if (data?.ok === true) {
        setIntegrationsUnlocked(true);
        sessionStorage.setItem('tt_integrations_unlocked', '1');
        setDevCode('');
        toast.success('Integration settings unlocked');
      } else {
        toast.error('Incorrect code');
      }
    } catch (e) {
      if (e?.context?.status === 429) toast.error('Too many attempts — wait a minute');
      else toast.error('Incorrect code');
    }
    setUnlocking(false);
  };

  const lockIntegrations = () => {
    sessionStorage.removeItem('tt_integrations_unlocked');
    setIntegrationsUnlocked(false);
  };

  const loadCurriculumDocs = async () => {
    try {
      const [{ data: docs }, { data: scopeRows }] = await Promise.all([
        supabase
          .from('policy_documents')
          .select('id, name, subject, level, status, check_enabled')
          .eq('organization_id', organizationId)
          .eq('document_type', 'curriculum')
          .order('created_at', { ascending: false }),
        supabase
          .from('curriculum_doc_scopes')
          .select('id, document_id, tutor_name, student_name')
          .eq('organization_id', organizationId),
      ]);
      setCurriculumDocs(docs || []);
      const grouped = {};
      (scopeRows || []).forEach(row => {
        (grouped[row.document_id] = grouped[row.document_id] || []).push(row);
      });
      setCurriculumScopes(grouped);
    } catch { /* ignore */ }
    setCurriculumDocsLoaded(true);
    loadParticipants();
  };

  const loadParticipants = async () => {
    // Fetch once per page visit
    if (participants.length) return;
    try {
      const { data } = await supabase.rpc('get_org_participants', { p_org: organizationId });
      setParticipants(data || []);
    } catch { /* ignore — coverage modal shows an empty list */ }
  };

  const toggleDocCheckEnabled = async (doc) => {
    const next = !(doc.check_enabled ?? true);
    // Optimistic update, reverted on failure
    setCurriculumDocs(prev => prev.map(d => d.id === doc.id ? { ...d, check_enabled: next } : d));
    try {
      const { error } = await supabase
        .from('policy_documents')
        .update({ check_enabled: next })
        .eq('id', doc.id);
      if (error) throw error;
      toast.success(next ? `"${doc.name}" will be used for checks` : `"${doc.name}" excluded from checks`);
    } catch (e) {
      setCurriculumDocs(prev => prev.map(d => d.id === doc.id ? { ...d, check_enabled: !next } : d));
      toast.error(e?.message || 'Failed to update document');
    }
  };

  // Coverage modal — the draft mirrors curriculum_doc_scopes semantics:
  // tutors[] = tutor-only rows, students[] = student-only rows, pairs[] = exact pairings.
  const openCoverage = (doc) => {
    const rows = curriculumScopes[doc.id] || [];
    setCoverageDraft({
      tutors: rows.filter(r => r.tutor_name && !r.student_name).map(r => r.tutor_name),
      students: rows.filter(r => !r.tutor_name && r.student_name).map(r => r.student_name),
      pairs: rows.filter(r => r.tutor_name && r.student_name).map(r => ({ tutor: r.tutor_name, student: r.student_name })),
    });
    setCoverageSearch({ tutors: '', students: '' });
    setPairForm({ tutor: '', student: '' });
    setCoverageDoc(doc);
  };

  const toggleDraftName = (kind, name) => {
    setCoverageDraft(prev => ({
      ...prev,
      [kind]: prev[kind].includes(name) ? prev[kind].filter(x => x !== name) : [...prev[kind], name],
    }));
  };

  const addDraftPair = () => {
    const { tutor, student } = pairForm;
    if (!tutor || !student) return;
    setCoverageDraft(prev => prev.pairs.some(p => p.tutor === tutor && p.student === student)
      ? prev
      : { ...prev, pairs: [...prev.pairs, { tutor, student }] });
    setPairForm({ tutor: '', student: '' });
  };

  const removeDraftPair = (idx) => {
    setCoverageDraft(prev => ({ ...prev, pairs: prev.pairs.filter((_, i) => i !== idx) }));
  };

  const saveCoverage = async () => {
    const doc = coverageDoc;
    if (!doc) return;
    setCoverageSaving(true);
    try {
      // Server-side matching is case-insensitive, so diff on lowercased keys.
      const keyOf = (t, s) => `${(t || '').toLowerCase()}|${(s || '').toLowerCase()}`;
      const existing = curriculumScopes[doc.id] || [];
      const existingKeys = new Set(existing.map(r => keyOf(r.tutor_name, r.student_name)));
      const draftRows = [
        ...coverageDraft.tutors.map(t => ({ tutor_name: t, student_name: null })),
        ...coverageDraft.students.map(s => ({ tutor_name: null, student_name: s })),
        ...coverageDraft.pairs.map(p => ({ tutor_name: p.tutor, student_name: p.student })),
      ];
      const draftKeys = new Set(draftRows.map(r => keyOf(r.tutor_name, r.student_name)));
      const toDelete = existing.filter(r => !draftKeys.has(keyOf(r.tutor_name, r.student_name))).map(r => r.id);
      const toInsert = draftRows
        .filter(r => !existingKeys.has(keyOf(r.tutor_name, r.student_name)))
        .map(r => ({ ...r, organization_id: organizationId, document_id: doc.id }));

      if (toDelete.length) {
        const { error } = await supabase.from('curriculum_doc_scopes').delete().in('id', toDelete);
        if (error) throw error;
      }
      let insertedRows = [];
      if (toInsert.length) {
        const { data, error } = await supabase
          .from('curriculum_doc_scopes')
          .insert(toInsert)
          .select('id, document_id, tutor_name, student_name');
        if (error) throw error;
        insertedRows = data || [];
      }
      setCurriculumScopes(prev => ({
        ...prev,
        [doc.id]: [...existing.filter(r => !toDelete.includes(r.id)), ...insertedRows],
      }));
      toast.success('Coverage saved');
      setCoverageDoc(null);
    } catch (e) {
      toast.error(e?.message || 'Failed to save coverage');
    }
    setCoverageSaving(false);
  };

  const tutorOptions = useMemo(() => {
    const m = new Map();
    participants.forEach(p => m.set(p.tutor_name, (m.get(p.tutor_name) || 0) + Number(p.lesson_count)));
    return [...m.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
  }, [participants]);

  const studentOptions = useMemo(() => {
    const m = new Map();
    participants.forEach(p => m.set(p.student_name, (m.get(p.student_name) || 0) + Number(p.lesson_count)));
    return [...m.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => a.name.localeCompare(b.name));
  }, [participants]);

  // Pairing builder only offers combinations that actually exist in sessions.
  const pairStudentOptions = useMemo(() =>
    participants
      .filter(p => p.tutor_name === pairForm.tutor)
      .map(p => ({ name: p.student_name, count: Number(p.lesson_count) }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  [participants, pairForm.tutor]);

  const coverageSummaryText = (draft) => {
    const parts = [];
    if (draft.tutors.length) parts.push(`lessons taught by ${draft.tutors.length} selected tutor${draft.tutors.length === 1 ? '' : 's'}`);
    if (draft.students.length) parts.push(`all lessons for ${draft.students.length} selected student${draft.students.length === 1 ? '' : 's'}`);
    if (draft.pairs.length) parts.push(`${draft.pairs.length} exact pairing${draft.pairs.length === 1 ? '' : 's'}`);
    if (!parts.length) return 'No restrictions — this document is checked against every lesson.';
    return `This document will be checked against ${parts.join(', plus ')}.`;
  };

  const removeDocScope = async (docId, scopeId) => {
    try {
      const { error } = await supabase
        .from('curriculum_doc_scopes')
        .delete()
        .eq('id', scopeId);
      if (error) throw error;
      setCurriculumScopes(prev => ({ ...prev, [docId]: (prev[docId] || []).filter(s => s.id !== scopeId) }));
      toast.success('Scope removed');
    } catch (e) {
      toast.error(e?.message || 'Failed to remove scope');
    }
  };

  const toggleCurriculumAutoCheck = async () => {
    const next = !(org?.curriculum_auto_check ?? true);
    setOrg(prev => ({ ...(prev || {}), curriculum_auto_check: next }));
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ curriculum_auto_check: next })
        .eq('id', organizationId);
      if (error) throw error;
      toast.success(next ? 'Automatic curriculum checks enabled' : 'Automatic curriculum checks disabled');
    } catch {
      setOrg(prev => ({ ...(prev || {}), curriculum_auto_check: !next }));
      toast.error('Failed to update automatic checks');
    }
  };

  // Rewrites this org's existing lesson names server-side (set_participant_id_names)
  // and makes future imports use the same "Name (ID 123)" format.
  const toggleParticipantIds = async () => {
    const next = !org?.show_participant_ids;
    setOrg(prev => ({ ...(prev || {}), show_participant_ids: next }));
    try {
      const { data, error } = await supabase.rpc('set_participant_id_names', { p_org: organizationId, p_enabled: next });
      if (error) throw error;
      toast.success(next ? `Participant IDs shown (${data ?? 0} lessons updated)` : `Participant IDs hidden (${data ?? 0} lessons updated)`);
    } catch (e) {
      setOrg(prev => ({ ...(prev || {}), show_participant_ids: !next }));
      toast.error(e?.message === 'not authorised' ? 'Only account admins can change this' : 'Failed to update participant names');
    }
  };

  const updateDataRegion = async (value) => {
    const previous = org?.data_region || 'au-sydney';
    if (value === previous) return;
    setOrg(prev => ({ ...(prev || {}), data_region: value }));
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ data_region: value })
        .eq('id', organizationId);
      if (error) throw error;
      const region = DATA_REGIONS.find(r => r.value === value);
      toast.success(`Data region updated to ${region ? region.label : value}`);
    } catch {
      setOrg(prev => ({ ...(prev || {}), data_region: previous }));
      toast.error('Failed to update data region');
    }
  };

  const profitMargin = defaults.default_billable_hourly_rate - defaults.default_tutor_hourly_rate;
  const profitPct = defaults.default_billable_hourly_rate > 0
    ? Math.round((profitMargin / defaults.default_billable_hourly_rate) * 100) : 0;

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Settings</h1>
        <div className={`${cardClass} h-48 animate-pulse`} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className={`text-2xl font-semibold font-[Manrope] tracking-tight ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
        Settings
      </h1>

      {/* Two-column layout: section side-nav (left) + content pane (right).
          On narrow screens (below md) the nav collapses to a horizontal scrollable strip above the content. */}
      <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-8">
        <nav className={`flex flex-row md:flex-col gap-1 w-full md:w-[210px] md:flex-shrink-0 md:sticky md:top-6 overflow-x-auto md:overflow-visible pb-2 md:pb-0 border-b md:border-b-0 ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
          {TABS.filter(tab => tab.id !== 'api' || developerAccess).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-2 rounded-lg text-sm text-left whitespace-nowrap flex-shrink-0 transition-colors ${
                activeTab === tab.id
                  ? isDark
                    ? 'bg-[#18181b] text-[#7662ac] font-medium'
                    : 'bg-[#586f8d]/10 text-[#586f8d] font-medium'
                  : isDark
                    ? 'text-[#a1a1aa] hover:text-white hover:bg-[#121212]'
                    : 'text-[#555555] hover:text-[#1a1a1a] hover:bg-[#f0f0f0]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0 w-full space-y-6">

      {/* GENERAL TAB */}
      {activeTab === 'general' && (
        <div className="space-y-5">
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Organisation Profile</h3>

            {/* Logo */}
            <div className="mb-5">
              <label className={labelClass}>Company Logo</label>
              <div className="flex items-center gap-4">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo" className="w-16 h-16 rounded-lg object-cover border border-[#27272a]" />
                ) : (
                  <div className={`w-16 h-16 rounded-lg flex items-center justify-center ${isDark ? 'bg-[#18181b]' : 'bg-[#f5f5f5]'}`}>
                    <Upload className={`h-5 w-5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`} />
                  </div>
                )}
                <label className={`cursor-pointer px-3 py-1.5 rounded-lg text-sm border ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:bg-[#f5f5f5]'}`}>
                  Upload
                  <input type="file" accept="image/*" className="hidden" onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); }
                  }} />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Organisation Name</label>
                <input className={inputClass} value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Business Type</label>
                <input className={inputClass} value={profile.business_type} onChange={e => setProfile(p => ({ ...p, business_type: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Company Number</label>
                <input className={inputClass} value={profile.company_number} onChange={e => setProfile(p => ({ ...p, company_number: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Country</label>
                <select className={selectClass} value={profile.country} onChange={e => setProfile(p => ({ ...p, country: e.target.value }))}>
                  <option value="">Select...</option>
                  {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>Business Address</label>
                <input className={inputClass} value={profile.business_address} onChange={e => setProfile(p => ({ ...p, business_address: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Phone Number</label>
                <div className="flex gap-2">
                  <select className={`${selectClass} w-52`} value={profile.phone_country_code} onChange={e => setProfile(p => ({ ...p, phone_country_code: e.target.value }))}>
                    {PHONE_CODES.map(c => <option key={`${c.label}-${c.code}`} value={c.code}>{c.label}</option>)}
                  </select>
                  <input className={inputClass} value={profile.phone_number} onChange={e => setProfile(p => ({ ...p, phone_number: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className={labelClass}>Contact Email</label>
                <input className={inputClass} type="email" value={profile.contact_email} onChange={e => setProfile(p => ({ ...p, contact_email: e.target.value }))} />
              </div>
            </div>
          </div>
          <button
            onClick={saveGeneral}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* USAGE TAB */}
      {activeTab === 'usage' && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: 'Total Lessons (30d)', value: usageStats.totalLessons },
              { label: 'Total Minutes (30d)', value: usageStats.totalMinutes.toLocaleString() },
              { label: 'Avg Lessons/Day', value: usageStats.avgPerDay },
            ].map(s => (
              <div key={s.label} className={cardClass}>
                <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{s.label}</span>
                <div className={`text-2xl font-semibold font-[Manrope] mt-1 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{s.value}</div>
              </div>
            ))}
          </div>
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Daily Lesson Volume</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={usageChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#27272a' : '#e5e5e5'} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: isDark ? '#52525b' : '#999' }} />
                  <YAxis tick={{ fontSize: 11, fill: isDark ? '#52525b' : '#999' }} />
                  <Tooltip contentStyle={{ backgroundColor: isDark ? '#121212' : '#fff', border: `1px solid ${isDark ? '#27272a' : '#e5e5e5'}`, borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="lessons" stroke="#586f8d" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ANALYSIS TAB */}
      {activeTab === 'analysis' && (
        <div className="space-y-5">
          {/* Lateness */}
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Lateness Detection</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Minor Late (minutes)</label>
                <input type="number" className={inputClass} min={1} value={analysis.lateness_minor_minutes} onChange={e => setAnalysis(a => ({ ...a, lateness_minor_minutes: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Moderately Late (minutes)</label>
                <input type="number" className={inputClass} min={1} value={analysis.lateness_moderate_minutes} onChange={e => setAnalysis(a => ({ ...a, lateness_moderate_minutes: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Severely Late (minutes)</label>
                <input type="number" className={inputClass} min={1} value={analysis.lateness_severe_minutes} onChange={e => setAnalysis(a => ({ ...a, lateness_severe_minutes: e.target.value }))} />
              </div>
            </div>
          </div>

          {/* Indicator Weights */}
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Indicator Importance Weights</h3>
            <div className="space-y-3">
              {Object.entries(INDICATOR_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{label}</span>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(v => (
                      <button
                        key={v}
                        onClick={() => setAnalysis(a => ({ ...a, indicator_weights: { ...a.indicator_weights, [key]: v } }))}
                        className={`w-8 h-8 rounded text-xs font-medium transition-colors ${
                          (analysis.indicator_weights[key] || 3) === v
                            ? 'accent-gradient text-white'
                            : isDark ? 'bg-[#18181b] text-[#52525b] hover:text-white' : 'bg-[#f5f5f5] text-[#999] hover:text-[#1a1a1a]'
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Proposed Actions */}
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Proposed Actions</h3>
            <p className={`text-xs mb-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              Options shown in the Flagged Issues proposed action dropdown.
            </p>
            <div className="space-y-2">
              {issueOutcomes.map(opt => (
                <div key={opt.value} className="flex items-center gap-2">
                  <input
                    className={inputClass}
                    value={opt.label}
                    onChange={e => updateIssueOutcomeLabel(opt.value, e.target.value)}
                  />
                  <span className={`hidden md:inline text-[10px] w-40 truncate ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{opt.value}</span>
                  <button
                    type="button"
                    onClick={() => removeIssueOutcome(opt.value)}
                    disabled={opt.value === 'no_action'}
                    title={opt.value === 'no_action' ? 'Required for non-issue closure' : 'Remove action'}
                    className={`p-2 rounded-md transition-colors disabled:opacity-30 ${isDark ? 'text-[#52525b] hover:text-red-400 hover:bg-[#18181b]' : 'text-[#999] hover:text-red-500 hover:bg-[#f5f5f5]'}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-2">
                <input
                  className={inputClass}
                  value={newIssueOutcome}
                  onChange={e => setNewIssueOutcome(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addIssueOutcome(); } }}
                  placeholder="e.g. Parent follow-up required"
                />
                <button
                  type="button"
                  onClick={addIssueOutcome}
                  disabled={!newIssueOutcome.trim()}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
                >
                  <Plus className="h-4 w-4" /> Add
                </button>
              </div>
            </div>
          </div>

          <button
            onClick={saveAnalysis}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* SCORING TAB */}
      {activeTab === 'scoring' && (
        <div className="space-y-5">
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Star Score Formula</h3>
            <p className={`text-xs mb-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              Top-level weights must sum to 100%. Applied on Star Performers and tutor profile Rank.
            </p>

            <div className="space-y-3">
              {[
                { key: 'quality', label: 'Quality' },
                { key: 'volume', label: 'Volume (hours)' },
                { key: 'issue_free', label: 'Issue-free' },
                { key: 'punctuality', label: 'Punctuality' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-3">
                  <label className={`text-sm w-40 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{label}</label>
                  <input
                    type="range" min={0} max={100} step={1}
                    value={starWeights[key]}
                    onChange={e => setStarWeights(w => ({ ...w, [key]: parseInt(e.target.value) || 0 }))}
                    className="flex-1"
                  />
                  <input
                    type="number" min={0} max={100}
                    value={starWeights[key]}
                    onChange={e => setStarWeights(w => ({ ...w, [key]: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) }))}
                    className={`${inputClass} w-20 text-right`}
                  />
                  <span className={`text-xs w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>%</span>
                </div>
              ))}
            </div>

            <div className={`mt-4 flex items-center justify-between text-xs ${starValid ? isDark ? 'text-[#a1a1aa]' : 'text-[#666]' : 'text-red-500'}`}>
              <span>Total: {starTotal}%</span>
              {!starValid && <span>Must equal 100%</span>}
            </div>
          </div>

          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Quality Sub-weights</h3>
            <p className={`text-xs mb-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              How each of the 5 quality dimensions contributes within the Quality component. Must sum to 100%.
            </p>

            <div className="space-y-3">
              {[
                { key: 'clarity', label: 'Clarity' },
                { key: 'engagement', label: 'Engagement' },
                { key: 'adaptability', label: 'Adaptability' },
                { key: 'encouragement', label: 'Encouragement' },
                { key: 'structure', label: 'Structure' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-3">
                  <label className={`text-sm w-40 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{label}</label>
                  <input
                    type="range" min={0} max={100} step={1}
                    value={qualityDims[key]}
                    onChange={e => setQualityDims(d => ({ ...d, [key]: parseInt(e.target.value) || 0 }))}
                    className="flex-1"
                  />
                  <input
                    type="number" min={0} max={100}
                    value={qualityDims[key]}
                    onChange={e => setQualityDims(d => ({ ...d, [key]: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) }))}
                    className={`${inputClass} w-20 text-right`}
                  />
                  <span className={`text-xs w-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>%</span>
                </div>
              ))}
            </div>

            <div className={`mt-4 flex items-center justify-between text-xs ${qualityValid ? isDark ? 'text-[#a1a1aa]' : 'text-[#666]' : 'text-red-500'}`}>
              <span>Total: {qualityTotal}%</span>
              {!qualityValid && <span>Must equal 100%</span>}
            </div>
          </div>

          <div className={`${cardClass}`}>
            <h3 className={`text-sm font-medium mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Live Preview</h3>
            <p className={`text-xs leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{previewFormula}</p>
          </div>

          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Needs Attention Thresholds</h3>
            <p className={`text-xs mb-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              Controls which tutors surface on the Needs Attention tab and the KPI counters.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Late threshold (minutes)</label>
                <input type="number" min={0} className={inputClass} value={needsAttn.late_minutes}
                  onChange={e => setNeedsAttn(n => ({ ...n, late_minutes: parseInt(e.target.value) || 0 }))} />
                <p className={`text-[10px] mt-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>A session counts as "late" if the tutor was at least this many minutes late.</p>
              </div>
              <div>
                <label className={labelClass}>Show in Needs Attention if on-time rate is below (%)</label>
                <input type="number" min={0} max={100} className={inputClass} value={needsAttn.on_time_rate_below_pct}
                  onChange={e => setNeedsAttn(n => ({ ...n, on_time_rate_below_pct: parseInt(e.target.value) || 0 }))} />
              </div>
              <div>
                <label className={labelClass}>Low quality threshold (%)</label>
                <input type="number" min={0} max={100} className={inputClass} value={needsAttn.quality_below_pct}
                  onChange={e => setNeedsAttn(n => ({ ...n, quality_below_pct: parseInt(e.target.value) || 0 }))} />
                <p className={`text-[10px] mt-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Tutors below this overall quality % surface as "Low quality".</p>
              </div>
              <div>
                <label className={labelClass}>Open issues threshold</label>
                <input type="number" min={0} className={inputClass} value={needsAttn.min_open_issues}
                  onChange={e => setNeedsAttn(n => ({ ...n, min_open_issues: parseInt(e.target.value) || 0 }))} />
                <p className={`text-[10px] mt-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Tutors with this many or more open issues surface.</p>
              </div>
              <div>
                <label className={labelClass}>Evaluation window (days)</label>
                <input type="number" min={1} className={inputClass} value={needsAttn.window_days}
                  onChange={e => setNeedsAttn(n => ({ ...n, window_days: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
          </div>

          <button
            onClick={saveScoring}
            disabled={saving || !starValid || !qualityValid}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* DEFAULT METRICS TAB */}
      {activeTab === 'defaults' && (
        <div className="space-y-5">
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Financial Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Currency</label>
                <select className={selectClass} value={defaults.currency} onChange={e => setDefaults(d => ({ ...d, currency: e.target.value }))}>
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={labelClass}>Default Lesson Time (minutes)</label>
                <select className={selectClass} value={defaults.default_lesson_time_minutes} onChange={e => setDefaults(d => ({ ...d, default_lesson_time_minutes: e.target.value }))}>
                  {Array.from({ length: (240 - 15) / 5 + 1 }, (_, i) => 15 + i * 5).map(m => (
                    <option key={m} value={m}>{m} minutes</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Default Tutor Hourly Rate</label>
                <div className="relative">
                  <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>$</span>
                  <input type="number" className={`${inputClass} pl-7`} min={0} step={0.01} value={defaults.default_tutor_hourly_rate} onChange={e => setDefaults(d => ({ ...d, default_tutor_hourly_rate: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className={labelClass}>Default Billable Hourly Rate</label>
                <div className="relative">
                  <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>$</span>
                  <input type="number" className={`${inputClass} pl-7`} min={0} step={0.01} value={defaults.default_billable_hourly_rate} onChange={e => setDefaults(d => ({ ...d, default_billable_hourly_rate: e.target.value }))} />
                </div>
              </div>
            </div>
            <div className={`mt-4 p-3 rounded-lg ${isDark ? 'bg-[#18181b]' : 'bg-[#f9f9f9]'}`}>
              <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Profit Margin</span>
              <div className={`text-lg font-semibold font-[Manrope] ${profitMargin >= 0 ? isDark ? 'text-emerald-400' : 'text-emerald-600' : 'text-red-500'}`}>
                ${profitMargin.toFixed(2)} <span className="text-sm font-normal">({profitPct}%)</span>
              </div>
            </div>
          </div>
          <button
            onClick={saveDefaults}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {/* API TAB (developer accounts only) */}
      {activeTab === 'api' && developerAccess && <DeveloperApi />}

      {/* INTEGRATIONS TAB */}
      {activeTab === 'integrations' && (
        <div className="space-y-5">
          {/* Lock banner */}
          <div className={`rounded-lg border p-4 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-2">
                <LockIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} />
                <p className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                  Integration settings are managed with TutorTracer support. Contact{' '}
                  <a href="mailto:support@tutortracer.com" className={isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}>support@tutortracer.com</a>{' '}
                  to make changes — or unlock with a developer code.
                </p>
              </div>
              {integrationsUnlocked ? (
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500">Unlocked</span>
                  <button
                    onClick={lockIntegrations}
                    className={`text-xs underline ${isDark ? 'text-[#52525b] hover:text-[#a1a1aa]' : 'text-[#999] hover:text-[#666]'}`}
                  >
                    Lock
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="password"
                    className={`${inputClass} w-40`}
                    placeholder="Developer code"
                    value={devCode}
                    onChange={e => setDevCode(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUnlockIntegrations(); } }}
                  />
                  <button
                    onClick={handleUnlockIntegrations}
                    disabled={unlocking || !devCode.trim()}
                    className="px-3 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
                  >
                    {unlocking ? 'Checking...' : 'Unlock'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Lessonspace */}
          <div className={`${cardClass} ${integrationsUnlocked ? '' : 'opacity-60'}`}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Lessonspace</h3>
            <div className={`space-y-4 ${integrationsUnlocked ? '' : 'pointer-events-none'}`}>
              <div>
                <label className={labelClass}>API Key</label>
                <div className="relative">
                  <input
                    type={showLsKey ? 'text' : 'password'}
                    className={inputClass}
                    value={lsApiKey}
                    onChange={e => setLsApiKey(e.target.value)}
                    placeholder="Enter your Lessonspace API key"
                    disabled={!integrationsUnlocked}
                  />
                  <button disabled={!integrationsUnlocked} onClick={() => setShowLsKey(!showLsKey)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                    {showLsKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <span className={`text-sm ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Continuous Sync</span>
                  <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Automatically sync every 15 minutes</p>
                </div>
                <button
                  onClick={() => setLsSyncEnabled(!lsSyncEnabled)}
                  disabled={!integrationsUnlocked}
                  className={`w-10 h-5 rounded-full transition-colors relative ${lsSyncEnabled ? 'bg-[#586f8d]' : isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${lsSyncEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
              <button
                onClick={() => triggerSync('lessonspace')}
                disabled={syncing || !integrationsUnlocked}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:bg-[#f5f5f5]'} disabled:opacity-50`}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} /> Manual Sync
              </button>
            </div>
          </div>

          {/* Google Meet */}
          <div className={`${cardClass} ${integrationsUnlocked ? '' : 'opacity-60'}`}>
            <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Google Meet</h3>
            <p className={`text-xs mb-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Requires a Google Workspace service account with Meet transcripts enabled</p>
            <div className={`space-y-4 ${integrationsUnlocked ? '' : 'pointer-events-none'}`}>
              <div>
                <label className={labelClass}>Service Account JSON</label>
                <div className="relative">
                  <textarea
                    rows={5}
                    className={`${inputClass} font-mono pr-10 resize-y`}
                    style={{ WebkitTextSecurity: showMeetJson ? 'none' : 'disc' }}
                    value={meetConfig.service_account_json}
                    onChange={e => setMeetConfig(m => ({ ...m, service_account_json: e.target.value }))}
                    placeholder="Paste service account JSON"
                    disabled={!integrationsUnlocked}
                    spellCheck={false}
                  />
                  <button disabled={!integrationsUnlocked} onClick={() => setShowMeetJson(!showMeetJson)} className={`absolute right-3 top-3 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                    {showMeetJson ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {meetConfigured && !meetConfig.service_account_json && (
                  <p className={`text-[10px] mt-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>A service account is already configured. Paste a new JSON key to replace it.</p>
                )}
              </div>
              <div>
                <label className={labelClass}>Impersonate Subject</label>
                <input
                  type="email"
                  className={inputClass}
                  value={meetConfig.impersonate_subject}
                  onChange={e => setMeetConfig(m => ({ ...m, impersonate_subject: e.target.value }))}
                  placeholder="workspace-admin@yourdomain.com"
                  disabled={!integrationsUnlocked}
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={saveMeetCredentials}
                  disabled={meetSaving || !integrationsUnlocked || !meetConfig.service_account_json.trim() || !meetConfig.impersonate_subject.trim()}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
                >
                  <Save className="h-3.5 w-3.5" /> {meetSaving ? 'Saving...' : 'Save Credentials'}
                </button>
                <button
                  onClick={triggerMeetSync}
                  disabled={syncing || !integrationsUnlocked}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:bg-[#f5f5f5]'} disabled:opacity-50`}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} /> Manual Sync
                </button>
              </div>
            </div>
          </div>

          {/* Zoom */}
          <div className={`${cardClass} ${integrationsUnlocked ? '' : 'opacity-60'}`}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Zoom</h3>
            <p className={`text-xs mb-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Requires Zoom Pro with Cloud Recording enabled</p>
            <div className={integrationsUnlocked ? '' : 'pointer-events-none'}>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className={labelClass}>Account ID</label>
                <input className={inputClass} disabled={!integrationsUnlocked} value={zoomConfig.account_id} onChange={e => setZoomConfig(z => ({ ...z, account_id: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Client ID</label>
                <input className={inputClass} disabled={!integrationsUnlocked} value={zoomConfig.client_id} onChange={e => setZoomConfig(z => ({ ...z, client_id: e.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>Client Secret</label>
                <div className="relative">
                  <input
                    type={showZoomSecret ? 'text' : 'password'}
                    className={inputClass}
                    value={zoomConfig.client_secret}
                    onChange={e => setZoomConfig(z => ({ ...z, client_secret: e.target.value }))}
                    disabled={!integrationsUnlocked}
                  />
                  <button disabled={!integrationsUnlocked} onClick={() => setShowZoomSecret(!showZoomSecret)} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                    {showZoomSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={() => triggerSync('zoom')}
              disabled={syncing || !integrationsUnlocked}
              className={`mt-4 flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:bg-[#f5f5f5]'} disabled:opacity-50`}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} /> Manual Sync
            </button>
            </div>
          </div>

          <button
            onClick={saveIntegrations}
            disabled={saving || !integrationsUnlocked}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Changes'}
          </button>

          {/* Recent Sync Jobs */}
          {syncJobs.length > 0 && (
            <div className={cardClass}>
              <h3 className={`text-sm font-medium mb-3 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Recent Sync Jobs</h3>
              <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
                {syncJobs.map(j => (
                  <div key={j.id} className="flex items-center justify-between py-2">
                    <div>
                      <span className={`text-sm ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{j.source || 'lessonspace'}</span>
                      <span className={`text-xs ml-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        {j.created_at ? new Date(j.created_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      j.status === 'completed' ? 'bg-emerald-500/10 text-emerald-500'
                      : j.status === 'running' || j.status === 'pending' ? 'bg-amber-500/10 text-amber-500'
                      : 'bg-red-500/10 text-red-500'
                    }`}>{j.status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* NOTIFICATIONS TAB */}
      {activeTab === 'notifications' && (
        <div className="space-y-5">
          {/* Alert Preferences */}
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Alert Preferences</h3>
            <div className="space-y-5">
              {[
                { key: 'highRiskAlert', label: 'High Risk Alert', desc: 'Immediate notifications for high-risk issues' },
                { key: 'emailMarketing', label: 'Product Updates', desc: 'Feature announcements and updates' },
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between">
                  <div>
                    <span className={`text-sm ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{item.label}</span>
                    <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{item.desc}</p>
                  </div>
                  <button
                    onClick={() => setNotifications(n => ({ ...n, [item.key]: !n[item.key] }))}
                    className={`w-10 h-5 rounded-full transition-colors relative ${notifications[item.key] ? 'bg-[#586f8d]' : isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${notifications[item.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              ))}
              <div>
                <label className={labelClass}>Timezone</label>
                <select className={selectClass} value={notifications.timezone} onChange={e => setNotifications(n => ({ ...n, timezone: e.target.value }))}>
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
            </div>
          </div>
          <button
            onClick={saveNotifications}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save Preferences'}
          </button>

          {/* Email Reports */}
          {[
            { type: 'daily', settings: dailyEmail, setSettings: setDailyEmail, icon: FileText, title: 'Daily Report', desc: 'Summary of lessons and issues from the last 24 hours' },
            { type: 'weekly', settings: weeklyEmail, setSettings: setWeeklyEmail, icon: BellIcon, title: 'Weekly Report', desc: 'Performance analytics for all tutors (sent weekly)' },
          ].map(({ type, settings, setSettings, icon: ReportIcon, title, desc }) => {
            const update = (key, val) => setSettings(p => ({ ...p, [key]: val }));
            const hasRecipient = settings.recipient_email?.trim();
            const toggleCls = (on) => `relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${on ? 'bg-[#586f8d]' : isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`;
            const dotCls = (on) => `inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-6' : 'translate-x-1'}`;
            const checkCls = (on) => `w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-colors ${on ? 'bg-[#586f8d] border-[#586f8d]' : isDark ? 'border-[#27272a] bg-transparent' : 'border-[#d0d0d0] bg-transparent'}`;
            return (
              <div key={type} className={cardClass}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <ReportIcon className={`h-5 w-5 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} strokeWidth={1.5} />
                    <div>
                      <h3 className={`text-base font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{title}</h3>
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{desc}</p>
                    </div>
                  </div>
                  <button type="button" onClick={() => update('enabled', !settings.enabled)} className={toggleCls(settings.enabled)}>
                    <span className={dotCls(settings.enabled)} />
                  </button>
                </div>
                {settings.enabled && (
                  <div className="space-y-5">
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Recipients</label>
                      {[
                        { email: 'recipient_email', name: 'recipient_name' },
                        { email: 'recipient_email_2', name: 'recipient_name_2' },
                        { email: 'recipient_email_3', name: 'recipient_name_3' },
                      ].map((r, i) => (
                        <div key={i} className="flex gap-2 mb-2">
                          <input type="email" placeholder={`Email${i === 0 ? ' (required)' : ''}`} value={settings[r.email] || ''} onChange={e => update(r.email, e.target.value)} className={`${inputClass} flex-1`} />
                          <input type="text" placeholder="Name" value={settings[r.name] || ''} onChange={e => update(r.name, e.target.value)} className={`${inputClass} w-40`} />
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-4">
                      <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Send at</label>
                        <input type="time" value={settings.send_time} onChange={e => update('send_time', e.target.value)} className={inputClass} style={{ width: '120px' }} />
                      </div>
                      {type === 'weekly' && (
                        <div>
                          <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Day</label>
                          <select value={settings.send_day} onChange={e => update('send_day', Number(e.target.value))} className={inputClass} style={{ width: '140px' }}>
                            {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Subject line</label>
                      <input type="text" value={settings.subject_line} onChange={e => update('subject_line', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Greeting message (optional)</label>
                      <input type="text" placeholder="e.g. Here's your daily update..." value={settings.greeting_message || ''} onChange={e => update('greeting_message', e.target.value)} className={inputClass} />
                    </div>
                    <div>
                      <label className={`block text-xs font-medium mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Include in email</label>
                      <div className="space-y-2">
                        {[
                          { key: 'include_new_sessions', label: 'New Sessions' },
                          { key: 'include_flagged_issues', label: 'Flagged Issues' },
                          { key: 'include_star_performers', label: 'Star Performers' },
                          { key: 'include_grade_distribution', label: 'Grade Distribution' },
                          { key: 'include_key_metrics', label: 'Key Metrics Summary' },
                        ].map(opt => (
                          <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                            <button type="button" onClick={() => update(opt.key, !settings[opt.key])} className={checkCls(settings[opt.key])}>
                              {settings[opt.key] && <Check className="w-3 h-3 text-white" />}
                            </button>
                            <span className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{opt.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div className={`pt-5 border-t flex items-center justify-between ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                      <button
                        onClick={() => handleEmailTest(type)}
                        disabled={!hasRecipient || emailTesting === type}
                        className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-40 ${
                          isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:border-[#ccc]'
                        }`}
                      >
                        {emailTesting === type ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                        Send test to all recipients
                      </button>
                      <div className="flex items-center gap-3">
                        {emailSaved === type && (
                          <span className="flex items-center gap-1 text-sm text-emerald-500">
                            <Check className="w-4 h-4" /> Saved
                          </span>
                        )}
                        <button
                          onClick={() => handleEmailSave(type)}
                          disabled={emailSaving === type}
                          className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                            isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'
                          }`}
                        >
                          {emailSaving === type ? (
                            <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Saving...</span>
                          ) : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ISSUE GUARDRAILS TAB */}
      {activeTab === 'rubric' && <QualityRubric embedded />}

      {activeTab === 'guardrails' && <IssueGuardrails embedded />}

      {/* CURRICULUM CHECKS TAB */}
      {activeTab === 'curriculum' && (
        <div className="space-y-5">
          {/* How it works */}
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>How curriculum checking works</h3>
            <ol className="space-y-3">
              {[
                <>Upload curriculum documents in <Link to="/library" className={`underline ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>Library &rarr; Curriculum</Link> (tag them with subject and level).</>,
                <>When a lesson is flagged with a teaching error, TutorTracer retrieves the most relevant passages from your curriculum for that lesson's subject and judges the flag against them.</>,
                <>The verdict appears on the <Link to="/issues" className={`underline ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>issue</Link> as a Curriculum Alignment panel &mdash; "Contradicts curriculum" strengthens the flag with a cited passage; "Aligns with curriculum" warns the flag may be a false positive.</>,
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded-full text-[10px] font-medium flex items-center justify-center ${isDark ? 'bg-[#7662ac]/20 text-[#7662ac]' : 'bg-[#586f8d]/10 text-[#586f8d]'}`}>{i + 1}</span>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>{step}</p>
                </li>
              ))}
            </ol>
            <p className={`text-xs mt-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              Checks are automatic for new lessons; on older issues use the Run Curriculum Check button.
            </p>
          </div>

          {/* Curriculum documents — per-document check toggle + scope rules */}
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-1">
              <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Curriculum documents</h3>
              {curriculumDocsLoaded && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${curriculumDocs.some(d => d.status === 'ready') ? 'bg-emerald-500/10 text-emerald-500' : 'bg-amber-500/10 text-amber-500'}`}>
                  {curriculumDocs.filter(d => d.status === 'ready').length} ready
                </span>
              )}
            </div>
            <p className={`text-xs mb-4 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
              A document with no coverage rules is checked against every lesson. Use Manage coverage to restrict it to particular tutors, particular students, or exact tutor&ndash;student pairings.
            </p>

            {curriculumDocsLoaded && curriculumDocs.length === 0 ? (
              <div className="p-3 rounded-lg bg-amber-500/10">
                <p className="text-sm text-amber-500">
                  No curriculum documents yet &mdash; teaching errors are assessed without curriculum context until you upload one.
                </p>
              </div>
            ) : (
              <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
                {curriculumDocs.map(doc => {
                  const scopes = curriculumScopes[doc.id] || [];
                  const tutorScopes = scopes.filter(s => s.tutor_name && !s.student_name);
                  const studentScopes = scopes.filter(s => !s.tutor_name && s.student_name);
                  const pairScopes = scopes.filter(s => s.tutor_name && s.student_name);
                  const checkOn = doc.check_enabled ?? true;
                  const statusCls = doc.status === 'ready' ? 'bg-emerald-500/10 text-emerald-500'
                    : doc.status === 'error' || doc.status === 'failed' ? 'bg-red-500/10 text-red-500'
                    : 'bg-amber-500/10 text-amber-500';
                  return (
                    <div key={doc.id} className="py-4 first:pt-0 last:pb-0">
                      {/* Header row */}
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-sm ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{doc.name}</span>
                        {doc.subject && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-[#7662ac]/20 text-[#7662ac]' : 'bg-[#586f8d]/10 text-[#586f8d]'}`}>{doc.subject}</span>
                        )}
                        {doc.level && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-[#18181b] text-[#a1a1aa]' : 'bg-[#f5f5f5] text-[#666]'}`}>{doc.level}</span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full ${statusCls}`}>{doc.status || 'unknown'}</span>
                        <div className="ml-auto flex items-center gap-2">
                          <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Used for checks</span>
                          <button
                            onClick={() => toggleDocCheckEnabled(doc)}
                            title={doc.status !== 'ready'
                              ? 'Takes effect once the document finishes processing'
                              : (checkOn ? 'Exclude this document from checks' : 'Use this document for checks')}
                            className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${checkOn ? 'bg-[#586f8d]' : isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}
                          >
                            <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${checkOn ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      </div>
                      {doc.status !== 'ready' && checkOn && (
                        <p className={`text-[10px] mt-1 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                          Not ready yet &mdash; checks apply once processing completes.
                        </p>
                      )}

                      {/* Coverage summary — grouped chips, managed in the coverage modal */}
                      <div className="mt-3 space-y-1.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>Applies to:</span>
                          {scopes.length === 0 && (
                            <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-[#18181b] text-[#a1a1aa]' : 'bg-[#f5f5f5] text-[#666]'}`}>All lessons</span>
                          )}
                          <button
                            onClick={() => openCoverage(doc)}
                            className={`ml-auto text-xs px-3 py-1.5 rounded-lg border font-medium transition-colors ${isDark ? 'border-[#27272a] text-[#a1a1aa] hover:text-white hover:bg-[#18181b]' : 'border-[#e5e5e5] text-[#666] hover:text-[#1a1a1a] hover:bg-[#f5f5f5]'}`}
                          >
                            Manage coverage
                          </button>
                        </div>
                        {[
                          { label: 'Tutors', rows: tutorScopes, text: s => s.tutor_name },
                          { label: 'Students', rows: studentScopes, text: s => s.student_name },
                          { label: 'Pairings', rows: pairScopes, text: s => `${s.tutor_name} → ${s.student_name}` },
                        ].filter(g => g.rows.length > 0).map(g => (
                          <div key={g.label} className="flex flex-wrap items-center gap-2">
                            <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{g.label} ({g.rows.length}):</span>
                            {g.rows.map(s => (
                              <span key={s.id} className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-[#7662ac]/20 text-[#7662ac]' : 'bg-[#586f8d]/10 text-[#586f8d]'}`}>
                                {g.text(s)}
                                <button
                                  onClick={() => removeDocScope(doc.id, s.id)}
                                  title="Remove"
                                  className="hover:text-red-500 transition-colors"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <Link to="/library" className={`inline-block mt-4 text-xs underline ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`}>
              Manage in Library
            </Link>
          </div>

          {/* Automatic checks toggle */}
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Automatic checks</h3>
            <div className="flex items-center justify-between gap-4">
              <div>
                <span className={`text-sm ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Check new teaching-error flags against curriculum automatically</span>
                <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>When off, checks only run manually from an issue.</p>
              </div>
              <button
                onClick={toggleCurriculumAutoCheck}
                className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${(org?.curriculum_auto_check ?? true) ? 'bg-[#586f8d]' : isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}
              >
                <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${(org?.curriculum_auto_check ?? true) ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>

          {/* Coverage modal — drafts tutors/students/pairings, writes only on Save */}
          {coverageDoc && (
            <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 px-4" onClick={() => !coverageSaving && setCoverageDoc(null)}>
              <div className="fixed inset-0 bg-black/50" />
              <div
                className={`relative z-10 w-full max-w-4xl max-h-[85vh] flex flex-col rounded-xl border shadow-2xl ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}
                onClick={e => e.stopPropagation()}
              >
                <div className={`flex items-center justify-between px-6 pt-5 pb-4 border-b ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                  <div className="min-w-0">
                    <h2 className={`text-lg font-semibold font-[Manrope] truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>
                      Coverage — {coverageDoc.name}
                    </h2>
                    <p className={`text-xs mt-0.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                      A lesson is checked against this document if it matches <span className="font-medium">any</span> rule below. Select nothing to cover every lesson.
                    </p>
                  </div>
                  <button onClick={() => !coverageSaving && setCoverageDoc(null)} className={isDark ? 'text-[#52525b] hover:text-white' : 'text-[#999] hover:text-[#1a1a1a]'}>
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    {[
                      { kind: 'tutors', title: 'Tutors', hint: 'Applies to every lesson taught by these tutors.', options: tutorOptions, placeholder: 'Search tutors...' },
                      { kind: 'students', title: 'Students', hint: 'Applies to every lesson for these students.', options: studentOptions, placeholder: 'Search students...' },
                    ].map(panel => {
                      const q = (coverageSearch[panel.kind] || '').toLowerCase();
                      const visible = panel.options.filter(o => !q || o.name.toLowerCase().includes(q));
                      return (
                        <div key={panel.kind} className={`rounded-lg border ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                          <div className={`p-3 border-b ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                            <div className="flex items-center justify-between">
                              <h4 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{panel.title}</h4>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${coverageDraft[panel.kind].length ? (isDark ? 'bg-[#7662ac]/20 text-[#7662ac]' : 'bg-[#586f8d]/10 text-[#586f8d]') : (isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f5f5f5] text-[#999]')}`}>
                                {coverageDraft[panel.kind].length} selected
                              </span>
                            </div>
                            <p className={`text-xs mt-0.5 mb-2 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{panel.hint}</p>
                            <input
                              className={inputClass}
                              placeholder={panel.placeholder}
                              value={coverageSearch[panel.kind]}
                              onChange={e => setCoverageSearch(prev => ({ ...prev, [panel.kind]: e.target.value }))}
                            />
                          </div>
                          <div className={`max-h-56 overflow-y-auto divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#f0f0f0]'}`}>
                            {visible.length === 0 ? (
                              <p className={`px-3 py-4 text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                                {panel.options.length === 0 ? 'No names found in your lessons yet.' : 'No matches.'}
                              </p>
                            ) : visible.map(o => (
                              <label key={o.name} className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer ${isDark ? 'hover:bg-[#18181b]' : 'hover:bg-[#f9f9f9]'}`}>
                                <input
                                  type="checkbox"
                                  className="accent-[#586f8d]"
                                  checked={coverageDraft[panel.kind].includes(o.name)}
                                  onChange={() => toggleDraftName(panel.kind, o.name)}
                                />
                                <span className={`text-sm flex-1 truncate ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{o.name}</span>
                                <span className={`text-xs flex-shrink-0 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>{o.count} lesson{o.count === 1 ? '' : 's'}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pairings — only combinations that actually exist are offered */}
                  <div className={`rounded-lg border ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                    <div className={`p-3 border-b ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                      <div className="flex items-center justify-between">
                        <h4 className={`text-sm font-medium ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Tutor–student pairings</h4>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${coverageDraft.pairs.length ? (isDark ? 'bg-[#7662ac]/20 text-[#7662ac]' : 'bg-[#586f8d]/10 text-[#586f8d]') : (isDark ? 'bg-[#18181b] text-[#52525b]' : 'bg-[#f5f5f5] text-[#999]')}`}>
                          {coverageDraft.pairs.length} added
                        </span>
                      </div>
                      <p className={`text-xs mt-0.5 ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                        Applies only to lessons where this exact tutor teaches this exact student.
                      </p>
                    </div>
                    <div className="p-3 space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          className={`${inputClass} w-56`}
                          value={pairForm.tutor}
                          onChange={e => setPairForm({ tutor: e.target.value, student: '' })}
                        >
                          <option value="">Select tutor...</option>
                          {tutorOptions.map(o => <option key={o.name} value={o.name}>{o.name}</option>)}
                        </select>
                        <span className={`text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>→</span>
                        <select
                          className={`${inputClass} w-56`}
                          value={pairForm.student}
                          onChange={e => setPairForm(prev => ({ ...prev, student: e.target.value }))}
                          disabled={!pairForm.tutor}
                        >
                          <option value="">{pairForm.tutor ? 'Select student...' : 'Select a tutor first'}</option>
                          {pairStudentOptions.map(o => <option key={o.name} value={o.name}>{o.name} ({o.count} lesson{o.count === 1 ? '' : 's'})</option>)}
                        </select>
                        <button
                          onClick={addDraftPair}
                          disabled={!pairForm.tutor || !pairForm.student}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
                        >
                          <Plus className="h-3.5 w-3.5" /> Add pairing
                        </button>
                      </div>
                      {coverageDraft.pairs.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {coverageDraft.pairs.map((p, i) => (
                            <span key={`${p.tutor}|${p.student}`} className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${isDark ? 'bg-[#7662ac]/20 text-[#7662ac]' : 'bg-[#586f8d]/10 text-[#586f8d]'}`}>
                              {p.tutor} → {p.student}
                              <button onClick={() => removeDraftPair(i)} title="Remove pairing" className="hover:text-red-500 transition-colors">
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className={`flex items-center gap-3 px-6 py-4 border-t ${isDark ? 'border-[#27272a]' : 'border-[#e5e5e5]'}`}>
                  <p className={`text-xs flex-1 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                    {coverageSummaryText(coverageDraft)}
                  </p>
                  <button
                    onClick={() => setCoverageDoc(null)}
                    disabled={coverageSaving}
                    className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-[#a1a1aa] hover:bg-[#18181b]' : 'text-[#666] hover:bg-[#f5f5f5]'}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveCoverage}
                    disabled={coverageSaving}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
                  >
                    {coverageSaving ? (<><Loader2 className="h-4 w-4 animate-spin" /> Saving...</>) : 'Save coverage'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* DATA RESIDENCY TAB */}
      {activeTab === 'residency' && (
        <div className="space-y-5">
          {/* Lock banner */}
          <div className={`rounded-lg border p-3 ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-start gap-2">
                <LockIcon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}`} />
                <p className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>
                  Data residency is locked. Contact{' '}
                  <a href="mailto:support@tutortracer.com" className={isDark ? 'text-[#7662ac]' : 'text-[#586f8d]'}>support@tutortracer.com</a>{' '}
                  to change your data location.
                </p>
              </div>
              {integrationsUnlocked ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500">Unlocked</span>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="password"
                    className={`${inputClass} w-44`}
                    placeholder="Enter developer code"
                    value={devCode}
                    onChange={e => setDevCode(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleUnlockIntegrations(); } }}
                  />
                  <button
                    onClick={handleUnlockIntegrations}
                    disabled={unlocking || !devCode.trim()}
                    className="px-3 py-2 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90 disabled:opacity-50"
                  >
                    {unlocking ? 'Checking...' : 'Unlock'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Participant names — "Kieran (ID 4106467)" for platforms that only expose first names */}
          <div className={cardClass}>
            <h3 className={`text-sm font-medium mb-4 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Participant names</h3>
            <div className="flex items-center justify-between gap-4">
              <div>
                <span className={`text-sm ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Show platform user ID next to names</span>
                <p className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                  e.g. "Kieran (ID 4106467)". Keeps tutors and students with the same first name separate across every page, report and the API.
                </p>
              </div>
              <button
                onClick={toggleParticipantIds}
                aria-label="Show platform user ID next to names"
                className={`w-10 h-5 rounded-full transition-colors relative flex-shrink-0 ${org?.show_participant_ids ? 'bg-[#586f8d]' : isDark ? 'bg-[#27272a]' : 'bg-[#e5e5e5]'}`}
              >
                <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${org?.show_participant_ids ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
          </div>

          {/* Data location card intentionally hidden (data residency is covered in customer agreements). */}
        </div>
      )}

      {/* TEAM TAB */}
      {activeTab === 'team' && (
        <div className="space-y-5">
          <div className={cardClass}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-sm font-medium ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Team Members</h3>
              <button
                onClick={() => setShowAddMember(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90"
              >
                <Plus className="h-3.5 w-3.5" /> Add Member
              </button>
            </div>

            {/* Add Member Form */}
            {showAddMember && (
              <div className={`mb-4 p-4 rounded-lg border ${isDark ? 'border-[#27272a] bg-[#18181b]' : 'border-[#e5e5e5] bg-[#f9f9f9]'}`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="md:col-span-2">
                    <label className={labelClass}>Email</label>
                    <input className={inputClass} type="email" value={newMemberEmail} onChange={e => setNewMemberEmail(e.target.value)} placeholder="member@example.com" />
                  </div>
                  <div>
                    <label className={labelClass}>Role</label>
                    <select className={selectClass} value={newMemberRole} onChange={e => setNewMemberRole(e.target.value)}>
                      {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={async () => {
                      try {
                        await supabase.functions.invoke('invite-user', { body: { email: newMemberEmail, role: newMemberRole, organizationId } });
                        toast.success('Invitation sent');
                        setShowAddMember(false);
                        setNewMemberEmail('');
                        loadTeam();
                      } catch { toast.error('Failed to invite'); }
                    }}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium text-white accent-gradient hover:opacity-90"
                  >
                    Send Invite
                  </button>
                  <button onClick={() => setShowAddMember(false)} className={`px-3 py-1.5 rounded-lg text-sm ${isDark ? 'text-[#a1a1aa] hover:bg-[#27272a]' : 'text-[#666] hover:bg-[#f0f0f0]'}`}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className={`divide-y ${isDark ? 'divide-[#27272a]' : 'divide-[#e5e5e5]'}`}>
              {teamMembers.map(m => (
                <div key={m.id} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>{m.email}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-[#18181b] text-[#a1a1aa]' : 'bg-[#f5f5f5] text-[#666]'}`}>
                      {m.role || 'member'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : ''}
                    </span>
                    {m.user_id !== user?.id && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Remove ${m.email} from the team?`)) return;
                          try {
                            const { error } = await supabase
                              .from('user_organizations')
                              .delete()
                              .eq('id', m.id);
                            if (error) throw error;
                            toast.success('Team member removed');
                            loadTeam();
                          } catch {
                            toast.error('Failed to remove member');
                          }
                        }}
                        className={`p-1 rounded transition-colors ${isDark ? 'text-[#52525b] hover:text-red-400 hover:bg-[#18181b]' : 'text-[#999] hover:text-red-500 hover:bg-[#f5f5f5]'}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {teamMembers.length === 0 && (
                <p className={`py-6 text-center text-sm ${isDark ? 'text-[#52525b]' : 'text-[#999]'}`}>No team members found</p>
              )}
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
